import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Grid, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import PageWithHeader from 'layouts/PageWithHeader/PageWithHeader';
import { ButtonComponent, CardComponent, LinkComponent, TableComponent } from 'components';
import { DEFAULT_TABLE_PARAMS } from 'components/TableComponent/constants';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { maintenanceModel } from 'models';
import { ROUTES } from 'routes/constants';
import { MigrationReport, MigrationReportRow } from 'types';
import { TableData, TableParams, TableRow } from 'types/other';
import { downloadFile } from 'utils';

import { ACTIVE_STATUSES, REPORTS_POLL_INTERVAL_MS } from './constants';
import { csvFilename, formatCount, formatReportDate, formatReportRange } from './helpers';
import { ReportStatusChip } from './ReportStatusChip';

const ROW_SEARCH_FIELDS: (keyof MigrationReportRow)[] = [
  'planogramId',
  'planogramName',
  'clientId',
  'planogramVersionId',
  'planogramVersionStatus',
  'stage',
  'message',
  'value',
];

const COPIED_FEEDBACK_MS = 1500;

const matchesSearch = (row: MigrationReportRow, search: string) =>
  ROW_SEARCH_FIELDS.some(field =>
    String(row[field] ?? '')
      .toLowerCase()
      .includes(search),
  );

export const MigrationReportDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { handleServerError } = useServerErrorActions();

  const [report, setReport] = useState<MigrationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tableParams, setTableParams] = useState<TableParams>({ ...DEFAULT_TABLE_PARAMS });

  const fetchReport = useCallback(
    () =>
      maintenanceModel
        .getMigrationReport(id)
        .then(run => {
          if (run.status !== 'finished' || !run.s3Key) return run;

          return maintenanceModel.getMigrationReportRows(csvFilename(run)).then(rows => ({ ...run, rows }));
        })
        .then(setReport)
        .catch(handleServerError)
        .finally(() => setLoading(false)),
    [id, handleServerError],
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const isActive = report !== null && ACTIVE_STATUSES.includes(report.status);

  useEffect(() => {
    if (!isActive) return undefined;

    const interval = setInterval(fetchReport, REPORTS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isActive, fetchReport]);

  const downloadCsv = () => {
    if (!report) return;

    setDownloading(true);
    maintenanceModel
      .downloadMigrationReportCsv(csvFilename(report))
      .then(blob => downloadFile(window.URL.createObjectURL(blob), csvFilename(report)))
      .catch(handleServerError)
      .finally(() => setDownloading(false));
  };

  const copyCsv = () => {
    if (!report) return;

    setCopying(true);
    maintenanceModel
      .downloadMigrationReportCsv(csvFilename(report))
      .then(blob => blob.text())
      .then(text => navigator.clipboard.writeText(text))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
      })
      .catch(handleServerError)
      .finally(() => setCopying(false));
  };

  const search = (tableParams.search ?? '').trim().toLowerCase();

  const filteredRows = useMemo(() => {
    const rows = report?.rows ?? [];
    return search ? rows.filter(row => matchesSearch(row, search)) : rows;
  }, [report, search]);

  const pageRows = useMemo(() => {
    const start = tableParams.page * tableParams.perPage;
    return filteredRows.slice(start, start + tableParams.perPage);
  }, [filteredRows, tableParams]);

  const handleParamsChange = (params: TableParams) => {
    const searchChanged = params.search !== tableParams.search;
    setTableParams({ ...tableParams, ...params, page: searchChanged ? 0 : params.page });
  };

  const tableData = useMemo<TableData>(
    () => ({
      headers: [
        t('pages.migrationReports.rows.planogramId'),
        t('pages.migrationReports.rows.planogramName'),
        t('pages.migrationReports.rows.clientId'),
        t('pages.migrationReports.rows.versionId'),
        t('pages.migrationReports.rows.versionStatus'),
        t('pages.migrationReports.rows.stage'),
        t('pages.migrationReports.rows.message'),
        t('pages.migrationReports.rows.value'),
      ],
      rows: pageRows.map<TableRow>(row => ({
        cells: [
          <>{row.planogramId}</>,
          <>{row.planogramName}</>,
          <>{row.clientId}</>,
          <>{row.planogramVersionId}</>,
          <>{row.planogramVersionStatus}</>,
          <>{row.stage}</>,
          <span style={{ whiteSpace: 'pre-wrap' }}>{row.message}</span>,
          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{row.value ?? ''}</span>,
        ],
      })),
    }),
    [pageRows, t],
  );

  const summaryItems: [string, string][] = report
    ? [
        [t('pages.migrationReports.details.range'), formatReportRange(report)],
        [t('pages.migrationReports.table.planograms'), formatCount(report.planogramsCount)],
        [t('pages.migrationReports.table.versions'), formatCount(report.versionsCount)],
        [t('pages.migrationReports.table.skipped'), formatCount(report.skippedVersionsCount)],
        [t('pages.migrationReports.table.problems'), formatCount(report.errorsCount)],
        [t('pages.migrationReports.details.versionsWithProblems'), formatCount(report.versionsWithErrorsCount)],
        [t('pages.migrationReports.table.created'), formatReportDate(report.enqueuedAt)],
        [t('pages.migrationReports.details.started'), formatReportDate(report.startedAt)],
        [t('pages.migrationReports.table.finished'), formatReportDate(report.finishedAt)],
        [t('pages.migrationReports.details.jid'), report.jid ?? '-'],
      ]
    : [];

  const isFinished = report?.status === 'finished';
  const rowsCount = report?.rows?.length ?? 0;

  return (
    <PageWithHeader
      tabTitle={t('pages.migrationReports.details.title')}
      headerText={t('pages.migrationReports.details.title')}
    >
      <Grid container spacing={4} direction="column">
        <Grid item>
          <LinkComponent to={ROUTES.migrationReports}>{t('pages.migrationReports.details.backToList')}</LinkComponent>
        </Grid>
        <Grid item>
          <CardComponent
            title={
              <Grid container spacing={2} alignItems="center">
                <Grid item>{t('pages.migrationReports.details.summary')}</Grid>
                {report && (
                  <Grid item>
                    <ReportStatusChip status={report.status} />
                  </Grid>
                )}
              </Grid>
            }
            sm={12}
            isLoading={loading}
            useSpinner
          >
            {report && (
              <Grid container spacing={2} direction="column">
                {report.summary && (
                  <Grid item>
                    <Typography>{report.summary}</Typography>
                  </Grid>
                )}
                {report.error && (
                  <Grid item>
                    <Typography color="error" style={{ whiteSpace: 'pre-wrap' }}>
                      {report.error}
                    </Typography>
                  </Grid>
                )}
                <Grid item container spacing={2}>
                  {summaryItems.map(([label, value]) => (
                    <Grid item xs={6} sm={4} md={3} key={label}>
                      <Typography variant="caption" color="textSecondary">
                        {label}
                      </Typography>
                      <Typography>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
                {isFinished && (
                  <Grid item container spacing={1} justify="flex-end">
                    <Grid item>
                      <ButtonComponent
                        text={t(`pages.migrationReports.details.${copied ? 'copied' : 'copyCsv'}`)}
                        loading={copying}
                        onClick={copyCsv}
                      />
                    </Grid>
                    <Grid item>
                      <ButtonComponent
                        color="primary"
                        text={t('pages.migrationReports.details.downloadCsv')}
                        loading={downloading}
                        onClick={downloadCsv}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}
          </CardComponent>
        </Grid>
        {isFinished && (
          <Grid item>
            <CardComponent title={`${t('pages.migrationReports.details.problems')} (${rowsCount})`} sm={12}>
              {rowsCount === 0 ? (
                <div>{t('pages.migrationReports.details.noProblems')}</div>
              ) : (
                <TableComponent
                  data={tableData}
                  count={filteredRows.length}
                  tableParams={tableParams}
                  onParamsChange={handleParamsChange}
                  enableSearch
                />
              )}
            </CardComponent>
          </Grid>
        )}
      </Grid>
    </PageWithHeader>
  );
};
